import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import axios from 'axios';
import { Play, ChevronRight, ExternalLink } from 'lucide-react';
import { Button } from '../../components/ui/button';

const Home = () => {
  const { t, settings, language, API } = useApp();
  const [releases, setReleases] = useState([]);
  const [videos, setVideos] = useState([]);
  const [featured, setFeatured] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [releasesRes, videosRes, featuredRes] = await Promise.all([
          axios.get(`${API}/public/releases`),
          axios.get(`${API}/public/videos`),
          axios.get(`${API}/public/featured`)
        ]);
        setReleases(releasesRes.data.videos?.slice(0, 6) || []);
        setVideos(videosRes.data.videos?.slice(0, 4) || []);
        
        // Get first featured or first release
        const featuredItem = featuredRes.data.featured?.[0] || releasesRes.data.videos?.[0];
        setFeatured(featuredItem);
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [API]);

  const heroText = settings?.hero_text?.[language] || 'Music is the language of the soul';
  
  // Use uploaded hero image or fallback to default
  const heroImage = settings?.hero_image 
    ? (settings.hero_image.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${settings.hero_image}` : settings.hero_image)
    : 'https://images.pexels.com/photos/19502012/pexels-photo-19502012.jpeg?auto=compress&cs=tinysrgb&w=1920';

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url(${heroImage})`
          }}
        >
          {/* Overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-brand-void" />
          
          {/* Red Glow Effect */}
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-glow-red opacity-50" />
        </div>

        {/* Content */}
        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
          <h1 className="font-syne font-bold text-4xl sm:text-5xl lg:text-7xl text-white mb-6 reveal">
            <span className="text-gradient">GOLDENG</span>
          </h1>
          <p className="font-manrope text-lg md:text-xl text-white/70 mb-8 reveal" style={{ animationDelay: '0.2s' }}>
            {heroText}
          </p>
          
          {/* Featured Release CTA */}
          <div className="reveal" style={{ animationDelay: '0.4s' }}>
            {featured ? (
              <a 
                href={`https://www.youtube.com/watch?v=${featured.video_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 btn-primary glow-red"
                data-testid="hero-cta"
              >
                <Play className="w-5 h-5 fill-current" />
                {t('hero.cta', 'Listen Now')}
              </a>
            ) : (
              <Link 
                to="/releases"
                className="inline-flex items-center gap-3 btn-primary glow-red"
                data-testid="hero-cta"
              >
                <Play className="w-5 h-5 fill-current" />
                {t('hero.cta', 'Listen Now')}
              </Link>
            )}
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 rounded-full border-2 border-white/30 flex items-start justify-center p-2">
            <div className="w-1 h-2 bg-white/50 rounded-full animate-pulse" />
          </div>
        </div>
      </section>

      {/* Latest Releases Section */}
      <section className="section-padding bg-brand-void" data-testid="releases-section">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-12 reveal">
            <h2 className="font-syne font-bold text-2xl md:text-3xl text-white">
              {t('releases.title', 'Latest Releases')}
            </h2>
            <Link 
              to="/releases" 
              className="flex items-center gap-2 text-white/50 hover:text-brand-red transition-colors"
              data-testid="view-all-releases"
            >
              View All <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="aspect-video bg-brand-charcoal animate-pulse rounded-lg" />
              ))}
            </div>
          ) : releases.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
              {releases.map((release, index) => (
                <a
                  key={release.video_id}
                  href={`https://www.youtube.com/watch?v=${release.video_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group reveal card-hover"
                  style={{ animationDelay: `${index * 0.1}s` }}
                  data-testid={`release-${release.video_id}`}
                >
                  <div className="relative aspect-video rounded-lg overflow-hidden bg-brand-charcoal">
                    <img
                      src={release.thumbnail}
                      alt={release.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                      <div className="w-14 h-14 rounded-full bg-brand-red flex items-center justify-center glow-red">
                        <Play className="w-6 h-6 text-white fill-white" />
                      </div>
                    </div>
                    {release.featured && (
                      <span className="absolute top-2 left-2 px-2 py-1 bg-brand-gold text-white text-xs font-semibold rounded">
                        Featured
                      </span>
                    )}
                  </div>
                  <h3 className="mt-3 font-manrope text-sm md:text-base text-white/90 line-clamp-2 group-hover:text-brand-red transition-colors">
                    {release.title}
                  </h3>
                </a>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <p className="text-white/50">No releases yet. Check back soon!</p>
            </div>
          )}
        </div>
      </section>

      {/* Music Videos Section */}
      <section className="section-padding bg-brand-dark" data-testid="videos-section">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-12 reveal">
            <h2 className="font-syne font-bold text-2xl md:text-3xl text-white">
              {t('videos.title', 'Music Videos')}
            </h2>
            <Link 
              to="/videos" 
              className="flex items-center gap-2 text-white/50 hover:text-brand-red transition-colors"
              data-testid="view-all-videos"
            >
              View All <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="aspect-video bg-brand-charcoal animate-pulse rounded-lg" />
              ))}
            </div>
          ) : videos.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              {videos.map((video, index) => (
                <a
                  key={video.video_id}
                  href={`https://www.youtube.com/watch?v=${video.video_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group reveal card-hover"
                  style={{ animationDelay: `${index * 0.1}s` }}
                  data-testid={`video-${video.video_id}`}
                >
                  <div className="relative aspect-video rounded-lg overflow-hidden bg-brand-charcoal">
                    <img
                      src={video.thumbnail}
                      alt={video.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6">
                      <h3 className="font-syne font-semibold text-lg md:text-xl text-white group-hover:text-brand-red transition-colors">
                        {video.title}
                      </h3>
                    </div>
                    <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                      <ExternalLink className="w-5 h-5 text-white" />
                    </div>
                  </div>
                </a>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <p className="text-white/50">No videos yet. Check back soon!</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-brand-void relative overflow-hidden">
        <div className="absolute inset-0 bg-glow-red opacity-30" />
        <div className="max-w-3xl mx-auto text-center relative z-10 reveal">
          <h2 className="font-syne font-bold text-3xl md:text-4xl text-white mb-6">
            {settings?.cta_text?.[language] || 'Ready to collaborate?'}
          </h2>
          <Link to="/contact">
            <Button className="btn-primary" data-testid="cta-contact">
              {t('nav.contact', 'Contact')}
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
